import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { setSeries } from 'app/data-studio-redesign/helper/data-studio-signal.helper';
import { DevicesList, DSSignalItemData, MinMaxSignals, PreviewSerieType, SignalMultiDevicesList } from 'app/shared/models/data-studio.model';
import { CurrencyService } from 'app/shared/services/currency-service/currency.service';
import Highcharts from 'highcharts';

@Component({
    selector: 'app-signals-preview',
    templateUrl: './signals-preview.component.html',
    styleUrls: ['./signals-preview.component.scss'],
    standalone: false
})
export class SignalsPreviewComponent implements OnChanges {
  @Input() devicesList: DevicesList[] = [];
  @Input() signalMultiDevicesList: SignalMultiDevicesList[] = [];
  @Input() signalsData: [DSSignalItemData[], MinMaxSignals[]];
  @Input() multiSignalsData: [DSSignalItemData[], MinMaxSignals[]];

  @Output() closePreview = new EventEmitter();
  @Output() getData = new EventEmitter<{devicesIds: string[], signalsIds: string[], multiDevices?: boolean}>();

  selectedDevice: DevicesList = null;
  selectedSignal: SignalMultiDevicesList = null;
  devicesNames: string[] = [];
  signalsNames: string[] = [];
  multiDeviceLineChartData = [];
  selectedDeviceLabel: string = '';
  selectedSignalLabel: string = '';
  unitsSelected: string[] = [];
  unitsMultiDeviceSelected: string[] = [];
  allUnitsCount: number = 0;
  MAX_UNITS_COUNT: number = 2;
  selectedTab = 0;
  loadingData: boolean = true;

  chartOptions: Highcharts.Options;
  highcharts = Highcharts;
  chartRef: Highcharts.Chart | null = null;
  series: PreviewSerieType[];
  seriesMultiDevice: PreviewSerieType[];

  allSignalsSelected: boolean = false;
  allDevicesSelected: boolean = false;

  multiSignals = new UntypedFormControl();
  multiDevices = new UntypedFormControl();

  signals: string[] = [];
  devices: string[] = [];

  get multiSignalsText() { return this.multiSignals?.value?.join(', ') || ''; }
  get multiDevicesText() { return this.multiDevices?.value?.join(', ') || ''; }

  chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  constructor(
    private currency: CurrencyService,
    private translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.devicesList?.currentValue?.length) {
      this.devicesNames = this.devicesList.map(device => device.name);
      this.selectedDevice = this.devicesList.find(device => device.selected);
      this.allUnitsCount = [...new Set(this.selectedDevice?.signals.map(signal => signal.unit))].length;
      this.setNewDeviceData();
    }

    if (changes.signalMultiDevicesList?.currentValue?.length) {
      this.signalsNames = this.signalMultiDevicesList.map(signal => signal.displayName);
      this.selectedSignal = this.signalMultiDevicesList.find(device => device.selected);
      this.setNewSignalData();
    }

    if (changes.signalsData?.currentValue) {
      this.setChartData();
    }

    if (changes.multiSignalsData?.currentValue) {
      this.setMultiChartData();
    }
  }

  setChartData() {
    this.unitsSelected = [...new Set(this.selectedDevice?.signals.filter(signal => signal.selected).map(signal => signal.unit))];
    const hasMinMaxDataFromServer = this.signalsData?.length === 2 && this.signalsData[1][0]?.min;
    const data = hasMinMaxDataFromServer ? this.signalsData[0] : this.signalsData;
    const timestamp = data.map(item => item.Timestamp);
    this.series = setSeries(data as DSSignalItemData[], this.currency, this.translate, 'signal').map(item => {
      return {
        type: 'line',
        name: item.label,
        unit: item.unit,
        yAxis: this.unitsSelected.indexOf(item.unit),
        legendSymbol: 'rectangle',
        data: item.data.map((point, index) => [timestamp[index], point])
      };
    });
    this.loadingData = false;
  }

  setMultiChartData() {
    const hasMinMaxDataFromServer = this.multiSignalsData?.length === 2 && this.multiSignalsData[1][0]?.min;
    const data = hasMinMaxDataFromServer ? this.multiSignalsData[0] : this.multiSignalsData;
    const timestamp = data.map(item => item.Timestamp);
    this.seriesMultiDevice = setSeries(data as DSSignalItemData[], this.currency, this.translate, 'device').map(item => {
      return {
        type: 'line',
        name: item.label,
        unit: item.unit,
        legendSymbol: 'rectangle',
        data: item.data.map((point, index) => [timestamp[index], point])
      };
    });
    this.unitsMultiDeviceSelected = [this.series?.[0]?.unit];
    this.loadingData = false;
  }

  triggerOnePerOneSignal(signal: string) {
    this.allSignalsSelected = this.multiSignals.value.length === this.signals.length;
    this.updateSignalsSelection();
    if (this.isSignalSelected(signal) && [...new Set(this.unitsSelected)].length <= this.MAX_UNITS_COUNT ||
        !this.isSignalSelected(signal) && [...new Set(this.unitsSelected)].length < this.MAX_UNITS_COUNT) {
      this.loadingData = true;
      this.getNewData();
    }
  }

  triggerOnePerOneDevice() {
    this.loadingData = true;
    this.allDevicesSelected = this.multiDevices.value.length === this.devices.length;
    this.updateDevicesSelection();
    this.getNewMultiData();
  }

  triggerAllSignals({checked}) {
    this.loadingData = true;
    this.allSignalsSelected = checked;
    this.multiSignals.patchValue(checked ? this.signals : []);
    this.updateSignalsSelection();
    this.getNewData();
  }

  triggerAllDevices({checked}) {
    this.loadingData = true;
    this.allDevicesSelected = checked;
    this.multiDevices.patchValue(checked ? this.devices : []);
    this.getNewMultiData();
  }

  triggerDeviceChange({option}) {
    this.loadingData = true;
    this.devicesList.forEach(device => {
      device.selected = device.name === option;
      if (device.selected) {
        this.selectedDevice = device;
        this.setNewDeviceData();
      }
    });
    this.getNewData();
  }

  triggerSignalChange({option}) {
    this.loadingData = true;
    this.signalMultiDevicesList.forEach(signal => {
      signal.selected = signal.displayName === option;
      if (signal.selected) {
        this.selectedSignal = signal;
        this.setNewSignalData();
      }
    });
    this.getNewMultiData();
  }

  close() {
    this.closePreview.emit();
  }

  isSignalSelected(signal: string) {
    const unit = this.selectedDevice.signals.find(item => `${item.displayName} (${item.unit})` === signal)?.unit;
    return this.unitsSelected.includes(unit);
  }

  private setNewDeviceData() {
    this.selectedDeviceLabel = this.selectedDevice?.name;
    this.signals = this.selectedDevice?.signals.map(signal => `${signal.displayName} (${signal.unit})`) || [];
    const selectedSignals = this.signals.filter(signal => this.multiSignals.value?.includes(signal));
    this.multiSignals.patchValue(selectedSignals.length ? selectedSignals : [this.signals[0]]);
    this.updateSignalsSelection();
    this.getNewData();
    this.allSignalsSelected = this.selectedDevice.signals.every(signal => signal.selected);
  }

  private setNewSignalData() {
    this.selectedSignalLabel = this.selectedSignal?.displayName;
    this.devices = this.selectedSignal?.devices.map(device => device.name) || [];
    const selectedDevices = this.devices.filter(device => this.multiDevices.value?.includes(device));
    this.multiDevices.patchValue(selectedDevices.length ? selectedDevices : [this.devices[0]]);
    this.updateDevicesSelection();
    this.getNewMultiData();
    this.allDevicesSelected = this.selectedSignal.devices.every(device => device.selected);
  }

  private updateSignalsSelection() {
    this.selectedDevice.signals.forEach(signal => {signal.selected = this.multiSignals.value.includes(`${signal.displayName} (${signal.unit})`)});
    this.unitsSelected = [...new Set(this.selectedDevice.signals.filter(signal => signal.selected).map(signal => signal.unit))];
  }

  private updateDevicesSelection() {
    this.selectedSignal.devices.forEach(device => {device.selected = this.multiDevices.value.includes(device.name)});
  }

  private getNewData() {
    const signalsIds = this.selectedDevice.signals.filter(signal => signal.selected).map(signal => signal.name);
    this.getData.emit({devicesIds: [this.selectedDevice.id], signalsIds});
  }

  private getNewMultiData() {
    const devicesIds = this.selectedSignal?.devices.filter(device => this.multiDevices.value?.includes(device.name)).map(device => device.id) || [];
    this.getData.emit({devicesIds, signalsIds: [this.selectedSignal?.name], multiDevices: true});
  }

}